import type { ComponentPropsWithoutRef } from "react";

/**
 * What survives sanitisation: digits, and in decimal mode the first "." — a
 * "," counts as one, because some phone keypads only offer the comma.
 * Everything else is dropped, including the "e", "+" and "-" that
 * `type="number"` happily accepted.
 */
export function numericOnly(raw: string, decimal: boolean): string {
  let out = "";
  let dotSeen = false;
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") {
      out += ch;
    } else if (decimal && !dotSeen && (ch === "." || ch === ",")) {
      out += ".";
      dotSeen = true;
    }
  }
  return out;
}

interface Props
  extends Omit<
    ComponentPropsWithoutRef<"input">,
    "type" | "inputMode" | "onChange" | "value"
  > {
  value: string;
  onChange: (next: string) => void;
  /** false → whole numbers: digit-only keypad, decimal point refused. */
  decimal?: boolean;
}

/**
 * The one way to type a number into this app.
 *
 * Not `type="number"`, deliberately. A focused number input treats several
 * gestures as increment commands — arrow keys, the spinner buttons some
 * browsers draw inside the field, a wheel or trackpad pass, the up/down keys
 * some phone keyboards synthesise from swipe gestures — and each one silently
 * rewrites the value by `step` while the user is looking at the test vial,
 * not the screen. That is how an entered FC of 7.4 gets logged as 7.5. A text
 * input has no step machinery at all, so the whole class of drift is
 * unrepresentable. `inputMode` still raises the right phone keypad, which is
 * everything `type="number"` was actually buying us. See ADR 0018.
 *
 * Empty stays empty: "" means "not measured" (all reading columns are
 * nullable), so nothing here ever coerces a blank to zero.
 */
export default function NumericInput({ value, onChange, decimal = true, ...rest }: Props) {
  return (
    <input
      {...rest}
      type="text"
      inputMode={decimal ? "decimal" : "numeric"}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      value={value}
      onChange={(e) => {
        const clean = numericOnly(e.target.value, decimal);
        // React skips re-rendering when the sanitised value equals current
        // state, which would leave a rejected character sitting in the DOM.
        // Correct the node itself so the box never shows what was refused.
        if (clean !== e.target.value) e.target.value = clean;
        onChange(clean);
      }}
      // A long-press text selection dragged across the form is the one
      // remaining touch gesture that could edit a value; refuse both
      // directions.
      onDragStart={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    />
  );
}
